import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/DropMenu.css";
export default function CustomDropdown({
  multi = false,
  value,
  onChange,
  options = [],
  placeholder = "Select",
  leftIcon = null,
  rightIcon = null,
  className = "",
  openClassName = "",
  menuClassName = "",
  optionClassName = "",
  disabled = false,
  searchable = false,
  renderTrigger = null,
  renderMenu = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const normalized = (options || []).map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

  const filtered = useMemo(() => {
    if (!searchable) return normalized;

    const q = search.trim().toLowerCase();
    if (!q) return normalized;

    return normalized.filter((o) =>
      String(o.label ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [normalized, search, searchable]);

  useEffect(() => {
    if (!open) return;

    if (searchable) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;

    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const isMulti = multi && Array.isArray(value);
  const safeValue = Array.isArray(value) ? value : [];

  const selectedLabel = isMulti
    ? safeValue
        .map((v) => normalized.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ")
    : (normalized.find((o) => o.value === value)?.label ?? "");

  return (
    <div
      ref={wrapRef}
      className={`${className} ${open ? openClassName : ""}`.trim()}
      onClick={() => {
        if (disabled) return;

        setOpen((p) => {
          const next = !p;
          if (!next) setSearch("");
          return next;
        });
      }}
      role="button"
      tabIndex={0}
      aria-expanded={open}
    >
      {renderTrigger ? (
        renderTrigger({ open, selectedLabel, placeholder, disabled })
      ) : (
        <>
          {leftIcon && (
            <div className="filter-popup-container-rows-icon">{leftIcon}</div>
          )}

          <input
            className="filter-popup-container-rows-input-data"
            placeholder={placeholder}
            value={selectedLabel}
            readOnly
            disabled={disabled}
          />

          {rightIcon && (
            <div className="filter-popup-container-rows-icon">{rightIcon}</div>
          )}
        </>
      )}

      {open && (
        <div className={menuClassName} onClick={(e) => e.stopPropagation()}>
          {renderMenu ? (
            renderMenu({
              open,
              closeMenu: () => {
                setOpen(false);
                setSearch("");
              },
            })
          ) : (
            <>
              {searchable && (
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  placeholder="Search..."
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    all: "unset",
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 10px",
                    marginBottom: "6px",
                  }}
                />
              )}

              {filtered.length
                ? filtered.map((opt, idx) => {
                    const isSelected = isMulti && safeValue.includes(opt.value);

                    return (
                      <div
                        key={`${opt.value}-${idx}`}
                        className={`${optionClassName} ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (isMulti) {
                            const newValue = safeValue.includes(opt.value)
                              ? safeValue.filter((v) => v !== opt.value)
                              : [...safeValue, opt.value];

                            onChange?.(newValue);
                          } else {
                            onChange?.(opt.value);
                            setOpen(false);
                            setSearch("");
                          }
                        }}
                      >
                        {isMulti && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{ margin: "7px" }}
                          />
                        )}

                        <div style={{ width: "85%", display: "inline-block" }}>
                          {opt.label}
                        </div>
                      </div>
                    );
                  })
                : searchable && (
                    <div style={{ padding: "8px 10px" }}>No results</div>
                  )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
